import { StatProps, UsdPriceTextWithTooltip } from "@bridgesplit/ui";
import { LoansAggregate } from "@bridgesplit/abf-react";
import { formatPercent } from "@bridgesplit/utils";
import { COPY } from "app/constants";

import { useActivePortfolioRewardsSummary } from "./util";
import { PortfolioTableStats, RewardPoint } from "./common";

export default function LoopsStats({ aggregate }: { aggregate: LoansAggregate | undefined }) {
    const portfolioRewards = useActivePortfolioRewardsSummary();
    const loading = !aggregate || !portfolioRewards || portfolioRewards.isLoading;

    const totalDebtUsd = aggregate ? aggregate.principalUsd + aggregate.interestAccruedUsd : undefined;
    const netPositionUsd =
        aggregate && totalDebtUsd !== undefined ? Math.max(aggregate.collateralUsd - totalDebtUsd, 0) : undefined;

    // Daily net for a loop = collateral yield − debt interest − principal yield foregone.
    // The third term accounts for yield-bearing principals (JLP, mSOL) appreciating on the debt
    // leg; 0 for USDC. Matches the borrower formula in BorrowLoanStats.
    const dailyNetInterest = aggregate
        ? aggregate.dailyCollateralYieldUsd - aggregate.dailyInterestUsd - aggregate.dailyPrincipalYieldUsd
        : undefined;

    // Net APY for a leveraged position is return-on-equity: annualize the daily net and
    // divide by equity (net position). Undefined when equity is zero.
    const netApy =
        aggregate && dailyNetInterest !== undefined && netPositionUsd && netPositionUsd > 0
            ? (dailyNetInterest * 365) / netPositionUsd
            : undefined;

    // Standardized section-stats order: Total value → APY → Daily interest → Daily rewards.
    // Pending yield is loop-specific; appended after the standard columns, hidden when 0.
    const stats: StatProps[] = [
        {
            value: <UsdPriceTextWithTooltip textColor="body" rawUsdValue={netPositionUsd} loading={loading} />,
            caption: COPY.NET_POSITION_TERM
        },
        {
            value: formatPercent(netApy),
            caption: COPY.NET_APY_TERM,
            tooltip: COPY.NET_APY_TOOLTIP
        },
        {
            value: <UsdPriceTextWithTooltip textColor="body" rawUsdValue={dailyNetInterest} loading={loading} />,
            caption: COPY.NET_INCOME_TERM,
            tooltip: COPY.SECTION_DAILY_INTEREST_TOOLTIP
        },
        {
            value: <RewardPoint points={portfolioRewards?.data?.activeLoopDailyPoints ?? 0} variant="body1" />,
            caption: COPY.DAILY_REWARDS_TERM
        },
        {
            value: (
                <UsdPriceTextWithTooltip textColor="body" rawUsdValue={aggregate?.pendingYieldUsd} loading={loading} />
            ),
            caption: COPY.PENDING_YIELD_TERM,
            hide: !aggregate?.pendingYieldUsd
        }
    ].filter((stat) => !stat.hide);

    return <PortfolioTableStats stats={stats} loading={loading} />;
}
